import { useMutation } from "@apollo/client";
import {
  ReactionType,
  EditMessageReactionMutation,
  EditMessageReactionMutationVariables,
  MessageFragment,
  ReactionFragment,
  MessageThreadFilesDrawerQuery,
  MessageThreadFilesDrawerQueryVariables,
} from "gql/graphql";
import { useAuth } from "modules/providers/auth-provider";
import { usePubSub } from "modules/providers/pub-sub-provider";
import { useMemo } from "react";
import ObjectID from "bson-objectid";
import MESSAGE_THREAD_FILES_DRAWER_QUERY from "modules/messaging/drawers/files-drawer/_networking/message-thread-files-drawer";
import EDIT_MESSAGE_REACTION_MUTATION from "modules/messaging/_networking/edit-message-reaction";

const useEditMessageReaction = (
  message: MessageFragment,
  type: ReactionType,
  fileId?: string | null,
) => {
  const { me } = useAuth();
  const reactionList = message.reactionList;
  const { publish } = usePubSub();

  const hasMe = useMemo(() => {
    if (!me) return false;
    if (fileId) {
      return reactionList.some(
        (obj) =>
          obj.type === type && obj.userId === me.id && obj.fileId === fileId,
      );
    }
    return reactionList.some(
      (obj) => obj.type === type && obj.userId === me.id && !obj.fileId,
    );
  }, [fileId, me, reactionList, type]);

  return useMutation<
    EditMessageReactionMutation,
    EditMessageReactionMutationVariables
  >(EDIT_MESSAGE_REACTION_MUTATION, {
    variables: {
      id: message.id,
      type,
      isSet: !hasMe,
      fileId,
    },
    optimisticResponse: () => {
      if (!me) {
        return {
          editMessage: {
            __typename: "Message",
            ...message,
          },
        };
      }
      const newReactionList: ReactionFragment[] = hasMe
        ? reactionList.filter(
            (obj) =>
              !(
                obj.userId === me.id &&
                obj.type === type &&
                (obj.fileId === null ? undefined : obj.fileId) === fileId
              ),
          )
        : [
            ...reactionList,
            {
              __typename: "Reaction",
              id: new ObjectID().toHexString(),
              type,
              userId: me.id ?? "",
              fileId: fileId ?? null,
            },
          ];
      const _message = {
        ...message,
        reactionList: newReactionList,
      };
      publish("message-updated", _message);
      return {
        editMessage: {
          __typename: "Message",
          id: message.id,
          reactionList: newReactionList,
        },
      };
    },
    update: (cache, { data }) => {
      if (!data) return;
      const editMessage = data?.editMessage;
      if (!editMessage) {
        return;
      }

      cache.updateQuery<
        MessageThreadFilesDrawerQuery,
        MessageThreadFilesDrawerQueryVariables
      >(
        {
          query: MESSAGE_THREAD_FILES_DRAWER_QUERY,
          variables: {
            id: message.messageThreadId,
          },
        },
        (data) => {
          if (!data || !data.messageThread) {
            return data;
          }

          return {
            ...data,
            messageThread: {
              ...data.messageThread,
              fileList: data.messageThread?.fileList.map((file) => {
                if (file.id === fileId) {
                  return {
                    ...file,
                    reactionList: editMessage.reactionList.filter(
                      (reaction) => reaction.fileId === file.id,
                    ),
                  };
                }
                return file;
              }),
            },
          };
        },
      );
    },
  });
};

export default useEditMessageReaction;
